import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { JiraFFLContentGenerationResponse } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { fallbackCgsData } from '@/templates/paid-landing/utils/paid-jira-ai-ffl';

import {
	type KanbanPreviewCardData,
	type PreviewCardData,
	type ScrumPreviewCardData,
	TemplateOptionKanban,
	TemplateOptionScrum,
} from '../types';

type PreviewCardRowContents = {
	dueDate?: Date;
	itemStatus?: 'TO DO' | 'IN PROGRESS' | 'COMPLETE';
	summary?: string;
	type?: 'epic' | 'task';
};

type EpicRow = Omit<PreviewCardRowContents, 'type'> & { type: 'epic' };

const transformCgsToPreviewRows = (
	cgsData: JiraFFLContentGenerationResponse,
): PreviewCardRowContents[] => {
	const rows: PreviewCardRowContents[] = [];
	const { epic, childWorkItems } = cgsData.sampleIssues;

	if (epic) {
		rows.push({
			type: 'epic',
			summary: epic.epicName,
		});
	}

	childWorkItems.forEach((item) => {
		rows.push({
			type: 'task',
			summary: item.summary,
		});
	});

	return rows;
};

const formatAsKanban = (rows: PreviewCardRowContents[]): KanbanPreviewCardData => {
	const hasEpic = rows.length > 0 && rows[0]?.type === 'epic';
	return {
		previewType: TemplateOptionKanban,
		data: {
			cardContents: rows as [EpicRow, ...PreviewCardRowContents[]],
			firstRowIsEpic: hasEpic,
			rowCount: rows.length,
		},
	};
};

const formatAsScrum = (rows: PreviewCardRowContents[]): ScrumPreviewCardData => {
	const midpoint = Math.ceil(rows.length / 2);
	return {
		previewType: TemplateOptionScrum,
		data: {
			sprintCardContents: rows.slice(0, midpoint),
			sprintCardRowCount: midpoint,
			backlogCardContents: rows.slice(midpoint),
			backlogCardRowCount: rows.length - midpoint,
		},
	};
};

export const useCgsPreviewData = (
	cgsKey: string | undefined,
	template: typeof TemplateOptionScrum | typeof TemplateOptionKanban | undefined,
) => {
	const stargateClient = useBxpWebStargateClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [cgsData, setCgsData] = useState<JiraFFLContentGenerationResponse | undefined>(undefined);
	const [isLoading, setIsLoading] = useState(Boolean(cgsKey));

	const trackFetchError = useCallback(
		(error: unknown) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'failed',
				actionSubject: 'cgsPreviewDataLoad',
				attributes: {
					cgsKey,
					errorMessage: error instanceof Error ? error.message : 'Unknown error',
				},
			}).fire();
		},
		[createAnalyticsEvent, cgsKey],
	);

	useEffect(() => {
		if (!cgsKey) {
			return;
		}

		if (cgsKey === 'default') {
			setCgsData(fallbackCgsData);
			setIsLoading(false);
			return;
		}

		let cancelled = false;

		const fetchData = async () => {
			try {
				setIsLoading(true);
				const response = await stargateClient.getJiraFFLContentByCgsKey(cgsKey);
				if (!cancelled) {
					setCgsData(response);
					setIsLoading(false);
				}
			} catch (error) {
				if (!cancelled) {
					setIsLoading(false);
					trackFetchError(error);
				}
			}
		};

		fetchData();

		return () => {
			cancelled = true;
		};
	}, [cgsKey, stargateClient, trackFetchError]);

	const previewCardData: PreviewCardData | undefined = useMemo(() => {
		if (isLoading || !cgsData) {
			return undefined;
		}

		const rows = transformCgsToPreviewRows(cgsData);

		if (template === TemplateOptionScrum) {
			return formatAsScrum(rows);
		}

		return formatAsKanban(rows);
	}, [cgsData, isLoading, template]);

	return { previewCardData, isLoading, cgsData };
};
